import "./ArticleStickyBar.css";

import ShareIcon from "@assets/article/shareIcon.svg";
import SummariseIcon from "@assets/article/summariseIcon.svg";
import TranslateIcon from "@assets/article/translateIcon.svg";
import AISummaryCTA from "@components/Modules/AISummary/AISummaryCTA";
import TranslationCTA from "@components/Modules/Translation/TranslationCTA";
import { ShareSocialBar } from "@pages/Article/Components/ShareSocialBar";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import cx from "classnames";
import { useRef } from "react";

import { useStickyBarLayout } from "./hooks/useStickyBarLayout";
import { useStickyBarVisibility } from "./hooks/useStickyBarVisibility";

export type ArticleStickyBarProps = {
  article: ProcessedArticleData;
  pageNumber: number;
  isStatic?: boolean;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  onVisibilityChange?: (isVisible: boolean) => void;
  isActive?: boolean;
};

export default function ArticleStickyBar({
  article,
  pageNumber,
  isStatic = false,
  gsChannels,
  articleSlotTarget,
  onVisibilityChange,
  isActive = true,
}: ArticleStickyBarProps) {
  const barRef = useRef<HTMLDivElement>(null);

  const { setLayoutHeights } = useStickyBarLayout(barRef);

  const { isVisible, progress } = useStickyBarVisibility({
    articleId: article.id,
    isStatic,
    isActive,
    onVisibilityChange,
    onShow: setLayoutHeights,
  });

  const containerClasses = cx("v_stickyArticleNow", {
    v_articleActionsBar: isStatic,
    v_showStickyBar: isVisible && !isStatic,
  });

  return (
    <div className={containerClasses} ref={barRef}>
      <div className="v_articleRow">
        <div className="v_articleColumn v_titleColumn">
          <div className="v_articleTitle">
            <h3>{article.title}</h3>
          </div>
        </div>

        {article.aiSummary && (
          <div className="v_articleColumn v_aiSummarySection">
            <AISummaryCTA
              article={article}
              gsChannels={gsChannels}
              articleSlotTarget={articleSlotTarget}
              pageNumber={pageNumber}
              renderButton={(onClick) => (
                <button className="v_summariseBtn" onClick={onClick}>
                  <img
                    src={SummariseIcon}
                    alt="Summarise"
                    width={24}
                    height={24}
                  />
                  Summarise
                </button>
              )}
            />
          </div>
        )}

        {article.translated && (
          <div className="v_articleColumn v_translateSection">
            <TranslationCTA
              article={article}
              gsChannels={gsChannels}
              articleSlotTarget={articleSlotTarget}
              pageNumber={pageNumber}
              renderButton={(onClick) => (
                <button className="v_translateBtn" onClick={onClick}>
                  <img
                    src={TranslateIcon}
                    alt="Translate"
                    width={21}
                    height={21}
                  />
                  Translate
                </button>
              )}
            />
          </div>
        )}

        <div className="v_articleColumn v_shareArticle v_shareRow">
          <ShareSocialBar
            dateCreated={article.datetime}
            pageUrl={article.urlPath || ""}
            socialIconList={[
              "facebook",
              "telegram",
              "plainWhatsapp",
              "newsPost",
            ]}
            title={article.title}
            renderButton={(onClick) => (
              <button onClick={onClick}>
                <img src={ShareIcon} alt="Share" width={24} height={24} />
                Share
              </button>
            )}
          />
        </div>
      </div>

      {!isStatic && (
        <div className="v_pageMeter">
          <div className="v_pageProgress" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}
